import { useEffect, useState } from "react";
import { AlarmClock, Loader2, Trash2, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Modal, inp, lbl } from "../gear/GearBits";

/* Cancellation-deadline reminders — multi-interval alerts (14d/7d/3d/48h/24h/custom)
   with weekend-aware shifting (Sat/Sun alerts fire on Friday). Categories: hotel stays,
   air tickets (cash/miles refund), tours, car rentals, insurance, other. */

const shiftOffWeekend = (d) => {
  const x = new Date(d);
  if (x.getUTCDay() === 6) x.setUTCDate(x.getUTCDate() - 1);
  else if (x.getUTCDay() === 0) x.setUTCDate(x.getUTCDate() - 2);
  return x;
};
const INTERVAL_MS = { "14d": 14 * 864e5, "7d": 7 * 864e5, "3d": 3 * 864e5, "48h": 48 * 36e5, "24h": 24 * 36e5 };
const fmt = (d) => new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

export default function DeadlineReminderDialog({ seg, tripId, onClose }) {
  const defaultCategory = { hotel: "hotel_stay", flight: "flight_ticket", car: "car_rental", reservation: "tour_activity" }[seg?.mode] || "other";
  const [opts, setOpts] = useState(null);
  const [existing, setExisting] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    category: defaultCategory,
    title: seg ? [seg.ref, seg.from_place, seg.to_place && `→ ${seg.to_place}`].filter(Boolean).join(" ") || "Cancellation deadline" : "",
    date: (seg?.cancel_deadline || "").slice(0, 10),
    time: (seg?.cancel_deadline || "").slice(11, 16) || "23:59",
    refund_type: seg?.refund_type || "cash",
    intervals: ["24h"], custom_at: "",
    business_day_shift: true, in_app: true, email: true, notes: "",
  });

  const loadExisting = () => {
    api.get(`/deadline-reminders?entity_id=${seg?.id || ""}${tripId ? `&trip_id=${tripId}` : ""}`)
      .then((r) => setExisting(r.data.deadlines)).catch(() => setExisting([]));
  };
  useEffect(() => {
    api.get("/deadline-reminders/options").then((r) => setOpts(r.data)).catch(() => {});
    if (seg?.id) loadExisting(); else setExisting([]);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const toggleInterval = (id) => setF((p) => ({
    ...p, intervals: p.intervals.includes(id) ? p.intervals.filter((x) => x !== id) : [...p.intervals, id],
  }));

  const deadlineIso = f.date ? `${f.date}T${f.time || "23:59"}:00+00:00` : null;
  const preview = deadlineIso ? f.intervals.map((id) => {
    const at = new Date(new Date(deadlineIso).getTime() - INTERVAL_MS[id]);
    const shifted = f.business_day_shift ? shiftOffWeekend(at) : at;
    return { id, at: shifted, moved: shifted.getTime() !== at.getTime(), past: shifted < new Date() };
  }) : [];

  const save = async () => {
    if (!deadlineIso) return toast.error("Set the deadline date");
    if (f.intervals.length === 0 && !f.custom_at) return toast.error("Pick at least one alert");
    setBusy(true);
    try {
      const r = await api.post("/deadline-reminders", {
        category: f.category, title: f.title || "Cancellation deadline",
        deadline_at: deadlineIso,
        refund_type: f.category === "flight_ticket" ? f.refund_type : null,
        trip_id: tripId || null, entity_id: seg?.id || null,
        intervals: f.intervals, custom_at: f.custom_at ? `${f.custom_at}:00+00:00` : null,
        business_day_shift: f.business_day_shift, in_app: f.in_app, email: f.email, notes: f.notes,
      });
      const shifted = r.data.alerts.filter((a) => a.weekend_shifted).length;
      toast.success(`${r.data.alerts.length} alert${r.data.alerts.length > 1 ? "s" : ""} scheduled${shifted ? ` (${shifted} moved off the weekend to Friday)` : ""}`);
      loadExisting();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not schedule reminders");
    } finally { setBusy(false); }
  };

  const cancelGroup = async (gid) => {
    await api.delete(`/deadline-reminders/group/${gid}`);
    toast.success("Reminders cancelled");
    loadExisting();
  };

  return (
    <Modal title="Cancellation deadline reminders" onClose={onClose}>
      <div className="space-y-4" data-testid="deadline-dialog">
        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className={lbl}>Category</label>
            <select value={f.category} onChange={set("category")} className={inp} data-testid="deadline-category">
              {(opts?.categories || []).map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select></div>
          <div><label className={lbl}>What is it for?</label>
            <input value={f.title} onChange={set("title")} className={inp} data-testid="deadline-title" placeholder="Hilton Tokyo / AA100 JFK→LHR" /></div>
          <div><label className={lbl}>Deadline (UTC)</label>
            <div className="flex gap-2">
              <input type="date" value={f.date} onChange={set("date")} className={inp} data-testid="deadline-date" />
              <input type="time" value={f.time} onChange={set("time")} className={inp} data-testid="deadline-time" />
            </div></div>
          {f.category === "flight_ticket" && (
            <div><label className={lbl}>Refund as</label>
              <select value={f.refund_type} onChange={set("refund_type")} className={inp} data-testid="deadline-refund">
                {(opts?.refund_types || []).map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select></div>
          )}
        </div>

        <div>
          <label className={lbl}>Alert me</label>
          <div className="flex flex-wrap gap-2">
            {(opts?.intervals || []).map((i) => (
              <button key={i.id} type="button" onClick={() => toggleInterval(i.id)} data-testid={`deadline-interval-${i.id}`}
                className={`px-3 py-1.5 text-xs font-700 border ${f.intervals.includes(i.id) ? "bg-[#4285F4] text-white border-[#4285F4]" : "bg-white border-zinc-300 text-zinc-600"}`}>
                {i.label}
              </button>
            ))}
            <input type="datetime-local" value={f.custom_at} onChange={set("custom_at")}
              className="border border-zinc-300 px-2 py-1 text-xs" title="Custom alert time" data-testid="deadline-custom" />
          </div>
          <label className="flex items-center gap-2 mt-2 text-xs text-zinc-700 cursor-pointer">
            <input type="checkbox" checked={f.business_day_shift}
              onChange={(e) => setF((p) => ({ ...p, business_day_shift: e.target.checked }))} data-testid="deadline-weekend-shift" />
            Skip weekends — alerts landing on Sat/Sun fire on Friday instead (support desks work weekdays)
          </label>
          <div className="flex gap-4 mt-1.5 text-xs text-zinc-700">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={f.in_app} onChange={(e) => setF((p) => ({ ...p, in_app: e.target.checked }))} /> In-app bell
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={f.email} onChange={(e) => setF((p) => ({ ...p, email: e.target.checked }))} /> Email
            </label>
          </div>
        </div>

        {preview.length > 0 && (
          <div className="border border-zinc-200 bg-zinc-50 p-2.5 text-xs space-y-1" data-testid="deadline-preview">
            {preview.map((p) => (
              <div key={p.id} className={`flex items-center gap-1.5 ${p.past ? "text-zinc-400 line-through" : "text-zinc-700"}`}>
                <CalendarClock className="w-3.5 h-3.5" /> {fmt(p.at)}
                {p.moved && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5">moved to Friday</span>}
                {p.past && <span className="text-[10px]">(already past — will be skipped)</span>}
              </div>
            ))}
          </div>
        )}

        {existing?.length > 0 && (
          <div>
            <div className={lbl}>Scheduled deadline reminders</div>
            {existing.map((g) => (
              <div key={g.deadline_group} className="flex items-center gap-2 text-xs border border-zinc-200 bg-white px-2.5 py-1.5 mb-1" data-testid={`deadline-group-${g.deadline_group}`}>
                <AlarmClock className="w-3.5 h-3.5 text-teal-700 shrink-0" />
                <span className="flex-1 truncate">{g.title} — {g.alerts.filter((a) => a.status === "scheduled").length} alert(s), deadline {fmt(g.deadline_at)}</span>
                <button onClick={() => cancelGroup(g.deadline_group)} className="text-zinc-400 hover:text-rose-600" data-testid={`deadline-cancel-${g.deadline_group}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-zinc-300">Close</button>
          <button onClick={save} disabled={busy} data-testid="deadline-save"
            className="px-5 py-2 text-sm font-bold bg-teal-700 text-white hover:bg-teal-800 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 inline animate-spin" /> : <AlarmClock className="w-4 h-4 inline mr-1" />} Schedule alerts
          </button>
        </div>
      </div>
    </Modal>
  );
}
